import { Module, VuexModule, Mutation, Action } from "vuex-module-decorators";
import BotsPerExchange from "@/models/bots/BotsPerExchange";
import TradingBotStats from "@/models/TradingBotStats";
import Vue from "vue";
import TradeModel from "@/models/Trade";
import OpenOrder from "@/models/OpenOrder";
import TotalBotsStats from "@/models/bots/TotalBotStats";
import camelcaseKeys from "camelcase-keys";
import { Emit } from "@/api/apiHelper";
import Coin from "@/models/Coin";
import { showSuccessSnackbar } from "@/utils/snackbarUtils";
import store from "../store";

/**
 * 🧩 Module which provides state for the user's trading bots
 */
@Module
export default class BotsModule extends VuexModule {
  // 📦 The active and inactive bots per connected exchange
  public botsPerExchanges: BotsPerExchange[] = [];

  // 🚩 Flag for whether the current user can create new bots
  public canCreateNewBots: boolean = true;

  // 📦 The bot stats of the all trading bots
  public allBots: TradingBotStats[] = [];

  // 📦 The bot stats of the active trading bots
  public get activeBots(): TradingBotStats[] {
    if (!this.allBots || this.allBots.length == 0) {
      return [];
    }
    return this.allBots
      .filter((bot: TradingBotStats) => bot.enabled)
      .sort((a, b) => {
        return b.start_time - a.start_time;
      });
  }

  // 📦 Holds the number of active bots throughout all exchanges
  public get totalActiveBots(): number {
    return this.activeBots.length;
  }

  // 📦 The bot stats of the archived trading bots
  public get archivedBots(): TradingBotStats[] {
    if (!this.allBots || this.allBots.length == 0) {
      return [];
    }
    return this.allBots
      .filter((bot: TradingBotStats) => !bot.enabled)
      .sort((a, b) => {
        return b.stop_time - a.stop_time;
      });
  }

  // 📦 Holds the number of archived bots throughout all exchanges
  public get totalArchivedBots(): number {
    return this.archivedBots.length;
  }

  // 📦 The total starts from all bots combined
  public totalBotsStats: TotalBotsStats | undefined = undefined;

  // 📦 The total balance that is spread through all bots
  public get totalStartingBalance(): number {
    let totalBalance = 0;
    if (this.activeBots != null && this.activeBots.length > 0) {
      this.activeBots.forEach((bot: TradingBotStats) => {
        totalBalance += bot.startingBalance[1];
      });
    }
    return totalBalance;
  }

  // 📦 The total number of bots combined between active and archived
  public get totalBots(): number {
    return this.allBots.length;
  }

  public get getTradingBotStatsById(): (
    botId: string
  ) => TradingBotStats | undefined {
    return (botId: string) => {
      return this.allBots.find(
        (botStats: TradingBotStats) => botStats.uid === botId
      );
    };
  }

  // 🔨 Function which searches for a specific bots stats in the cached ones
  public get getTradingBotStatsByIds(): (
    ids: string[]
  ) => TradingBotStats[] | undefined {
    return (ids: string[]) =>
      this.allBots.filter((stats: TradingBotStats) => ids.includes(stats.uid));
  }

  /**
   * 👾 Mutation which sets the bots per exchange
   */
  @Mutation
  public setBotsPerExchanges(botsPerExchanges: BotsPerExchange[]) {
    this.botsPerExchanges = botsPerExchanges;
  }

  /**
   * 👾 Mutation which sets whether the current user can create new bots
   */
  @Mutation
  public setCanCreateNewBots(canCreateNewBots: boolean) {
    this.canCreateNewBots = canCreateNewBots;
  }

  /**
   * 👾 Mutation which sets all the bots
   */
  @Mutation
  public setAllBots(botsStats: TradingBotStats[]) {
    this.allBots = botsStats;
  }

  /**
   * 👾 Mutation which sets the combined bot stats
   */
  @Mutation
  public setTotalBotsStats(botsStats: TotalBotsStats) {
    this.totalBotsStats = botsStats;
  }

  /**
   * 🚀 Action which fetches the bots per exchange from the API
   */
  @Action
  public async fetchBotsPerExchanges() {
    const response = await Vue.prototype.$api.get("/api/v1/listbots");

    // Bots per exchange
    this.context.commit("setBotsPerExchanges", response.data);
  }

  /**
   * 🚀 Fetches the trading bots stats of the user
   */
  @Action
  public async fetchTradingBotStats() {
    const parameters = store.state.userPreferences.selectedDashboardTimeFrame
      ? {
        start_time:
          store.state.userPreferences.selectedDashboardTimeFrame * 86400,
      }
      : {};
    const response = await Vue.prototype.$api.get(
      "/api/v1/botstats",
      parameters
    );

    if (Object.entries(response.data).length == 0) {
      // No bots returned
      this.context.commit("setAllBots", []);
      this.context.dispatch("fetchTotalBotsStats");
    } else {
      // There are bots returned
      let modifiedResponse: TradingBotStats[] = [];
      for (let [key, value] of Object.entries(response.data)) {
        modifiedResponse.push(TradingBotStats.newInstance(value));
      }
      this.context.commit("setAllBots", modifiedResponse);
      this.context.dispatch("fetchTotalBotsStats");
    }
  }

  /**
   * 🔨 Calculates and sets the combined bot stats between all active bots
   * @param botStats
   * @returns
   */
  @Action
  public async fetchTotalBotsStats() {
    let totalCurrentBalanceInUse = 0;
    let totalTrades = 0;
    let totalBotProfit = 0;
    let totalBaHProfit = 0;
    let totalVolume = 0;
    let totalFees = 0;
    let combinedBotsROI = 0;
    let totalStartingBalance = 0;
    let openOrders: OpenOrder[] = [];

    if (this.activeBots.length > 0) {
      this.activeBots.forEach((value: TradingBotStats) => {
        totalStartingBalance += value.startingBalance[1];
        totalCurrentBalanceInUse += value.portfolioValue;
        totalBotProfit += value.bot_profit;
        totalBaHProfit += value.bah_profit;
        // TODO: 31/07/2022 Temporary excluding LUNC bots due to wrong fee calculation
        if (!value.market.includes("LUNC")) {
          totalFees += value.in_fees;
        }
        combinedBotsROI += value.roi;
        openOrders = openOrders.concat(value.open_orders);
        totalVolume += value.trade_volume;
        totalTrades += value.trades_count;
      });
    }

    let botsSummary: any;
    try {
      const response = await Vue.prototype.$api.get(
        "/api/v1/profilesummary",
        null,
        Emit.None
      );
      botsSummary = camelcaseKeys(response.data);
    } catch (error) { }

    // Calculate the coin's percentage value compared to the whole balance in use
    const totalCoinsValue: number = (Object.values(
      botsSummary.coins
    ) as number[]).reduce((a: any, b: any) => Number(a) + Number(b), 0);
    let coinsUsed: Coin[] = Object.entries(botsSummary.coins)
      .map(([coinName, coinValue]) => {
        return new Coin(
          coinName,
          Number(coinValue),
          (Number(coinValue) / totalCoinsValue) * 100,
          botsSummary.tokens[coinName]
        );
      })
      .sort((coinA: Coin, coinB: Coin) => coinB.value - coinA.value);

    let botsWithAtLeastTwoTrades = this.allBots.filter(
      (bot: TradingBotStats) => bot.trades_count > 1
    );

    this.context.commit(
      "setTotalBotsStats",
      new TotalBotsStats(
        totalCurrentBalanceInUse,
        totalTrades,
        totalStartingBalance > 0 && totalBotProfit != 0
          ? totalBotProfit / totalStartingBalance
          : 0,
        totalStartingBalance > 0 && totalBaHProfit != 0
          ? totalBaHProfit / totalStartingBalance
          : 0,
        totalStartingBalance,
        totalBotProfit,
        totalBaHProfit,
        totalVolume,
        totalFees,
        openOrders,
        coinsUsed ? coinsUsed : [],
        botsWithAtLeastTwoTrades
          .filter((bot: TradingBotStats) => bot.sharpe != 0)
          .map((bot: TradingBotStats) => bot.sharpe)
          .reduce((acc, v, i, a) => acc + v / a.length, 0),
        botsWithAtLeastTwoTrades
          .filter((bot: TradingBotStats) => bot.sortino != 0)
          .map((bot: TradingBotStats) => bot.sortino)
          .reduce((acc, v, i, a) => acc + v / a.length, 0),
        botsWithAtLeastTwoTrades
          .filter((bot: TradingBotStats) => bot.maximum_drawdown != 0)
          .map((bot: TradingBotStats) => bot.maximum_drawdown)
          .reduce((acc, v, i, a) => acc + v / a.length, 0)
      )
    );
  }

  /**
   * 🚮 Archives a bot locally in order to hide it from the active tab on the dashboard
   * @param botId
   */
  @Action
  public removeBotByIdLocally(botId: string) {
    const botToBeDeactivated: TradingBotStats = this.allBots.find(
      (bot: TradingBotStats) => {
        return bot.uid === botId;
      }
    )!!;
    botToBeDeactivated.enabled = false;
    botToBeDeactivated.stop_time = new Date().getTime() / 1000;
  }

  /**
   * 🚮 Deletes an archived bot both locally and on the server
   * @param botId
   */
  @Action
  public async removeArchivedBot(botId: string) {
    try {
      const response = await Vue.prototype.$api.post(
        "/api/v1/botvisible",
        {
          uid: botId,
        },
        Emit.All,
        true
      );
      this.allBots.splice(
        this.allBots.findIndex((archivedBot) => archivedBot.uid === botId),
        1
      );
      this.context.commit("setAllBots", this.allBots);
      showSuccessSnackbar("Successfully deleted an archived bot!");
    } catch (error) { }
  }

  /**
   * 🚀 Fetches the trade history
   */
  @Action
  public async fetchTradeHistory(botId: number | undefined) {
    let selectedDashboardTimeFrame =
      store.state.userPreferences.selectedDashboardTimeFrame;

    const response = await Vue.prototype.$api.get("/api/v1/bots/tradehistory", {
      bot_id: botId,
      start_time: selectedDashboardTimeFrame
        ? selectedDashboardTimeFrame * 86400
        : undefined,
    });

    let modifiedTradeLog = response.data
      .map((entry: any) => {
        entry.trade_log.forEach((trade: any) => {
          trade.bot_id = entry.bot_id;
          trade.market = entry.market;
          trade.strategy_name = entry.strategy_name;
        });

        entry.trade_log = TradeModel.transformTradeHistory(entry.trade_log, entry.starting_balance);
        return entry.trade_log;
      })
      .flat();

    modifiedTradeLog.sort(
      (a: TradeModel, b: TradeModel) =>
        new Date(Number(a.date)).getTime() - new Date(Number(b.date)).getTime()
    );

    return modifiedTradeLog;
  }
}
